import React from 'react';

import { Text } from '@/nova/components';
import { FEATURE_TONE_COLORS, VS_BAR_COLORS, type FeatureTone } from '@/nova/theme/tokens';
// Shared with the product root's bar on purpose: one rule for where a pill sits, on both surfaces.
import { labelClampPx, pillCentrePct } from '../../../../components/VsMarketBar/pill-centre';
import {
  Label,
  Marker,
  MarkerArrow,
  MarkerLabel,
  MarkerLabelBox,
  MarketFill,
  ProjectionFill,
  Root,
  PillGap,
  PillSlot,
  TierPill,
  Track,
  TrackArea,
  MARKER_CAP_PX,
  type MarkerAlign,
} from './BenchmarkBar.styles';

export type BenchmarkBarProps = {
  /** 0-100. Where you stand NOW ("You" marker). 0 = inactive / not owned. Stays fixed while picking. */
  now: number;
  /** 0-100. Where the SELECTED tier would put you ("could be"): the projection fill + tier pill,
   *  which slide as the picker switches tier. */
  couldBe: number;
  /** 0-100. Fixed market reference marker. */
  market: number;
  /** Tone of the "You" (now) marker — reflects the current state (inactive = danger, else level tone).
   *  The only tone this bar takes: the projection fill and the tier pill are uniform (see
   *  ProjectionFill), so the state is stated once, by this marker and the page's status line. */
  nowTone: FeatureTone;
  /** Selected tier name shown on the pill riding the projection tip. */
  tierLabel: string;
  /** When false the fills start empty (pre-mount) then grow to their real width. */
  animate: boolean;
  className?: string;
};

/** You and Market sit on the same spot — show one combined marker. */
const COMBINE_WITHIN = 2;

/** Keep an edge marker's label from clipping: near the left it runs right, near the right it runs
 *  left, otherwise it centres over the arrow. */
const alignFor = (left: number): MarkerAlign => {
  if (left <= 10) return 'start';
  if (left >= 90) return 'end';
  return 'center';
};

const ArrowUp = ({ color }: { color: string }): React.ReactElement => (
  <MarkerArrow viewBox="0 0 12 8" aria-hidden $color={color}>
    <path d="M6 1.5L10.5 6.5L1.5 6.5Z" />
  </MarkerArrow>
);

/**
 * Three-point "You vs Market" bar for the product feature detail: a fixed **You (now)** marker
 * (where you stand today, 0 when inactive / at a lower tier), a **Market** reference marker, and a
 * **could-be** projection fill + tier-name pill that slides to whichever tier the picker has
 * selected. Speaks the find-my-pack vocabulary the shared VsMarketBar (the product root's feature
 * cards) and the configurator DetailTrack paint: uniform fill, market hatch, neutral pill riding the
 * tip, rounded arrow markers — so the same indicator reads as one design on all three surfaces.
 */
export const BenchmarkBar = ({
  now,
  couldBe,
  market,
  nowTone,
  tierLabel,
  animate,
  className,
}: BenchmarkBarProps): React.ReactElement => {
  const couldBeW = animate ? couldBe : 0;
  const marketW = animate ? market : 0;
  // Layering decided from the FINAL values (not the animating widths) so the market hatch's
  // z-order stays stable through the mount grow instead of flipping mid-animation.
  const onTop = couldBe >= market;
  const nowColor = FEATURE_TONE_COLORS[nowTone];
  const marketColor = VS_BAR_COLORS.marker;

  // When the picker sits on the tier the feature ALREADY runs on, the could-be pill and the "You"
  // marker mark the very same point, so they have to line up — the same rule the product root's
  // cards follow. The offset is only knowable from measurements (a px-sized label against a
  // percentage track), so the pill and track boxes are observed and the arithmetic is the shared
  // `pillCentrePct`. While the picker is on a DIFFERENT tier the two mark different things (what
  // you have vs what you could have) and the marker stays on its own value.
  const trackRef = React.useRef<HTMLDivElement>(null);
  const pillRef = React.useRef<HTMLSpanElement>(null);
  const labelRef = React.useRef<HTMLSpanElement>(null);
  const offRef = React.useRef<HTMLSpanElement>(null);
  const [boxes, setBoxes] = React.useState<{ pill: number; track: number; label: number; off: number }>({
    pill: 0,
    track: 0,
    label: 0,
    off: 0,
  });

  const onPillPoint = now > 0 && now === couldBe && !!tierLabel;
  const centred = onPillPoint ? pillCentrePct(couldBeW, boxes.pill, boxes.track) : null;
  const youAt = centred ?? now;
  // The arrow stays where it marks; a label that would hang off the bar slides instead. Applied on
  // the centred placements only — an edge-aligned column already runs its label inward, and this
  // clamp reasons about a label centred on its arrow. The merged "You and Market" label is the wide
  // one, and it renders on the NON-pill path whenever the picker previews another tier, which is
  // the page's default: without this it sat 16px off a 310px panel, at rest.
  const youAlign = alignFor(youAt);
  const arrowPx = centred == null ? (youAt / 100) * boxes.track - MARKER_CAP_PX : (youAt / 100) * boxes.track;
  const labelShift =
    centred != null || youAlign === 'center' ? labelClampPx(arrowPx, boxes.label, boxes.track) : 0;
  // You (now) and Market coincide — one combined marker. Compared on where the You marker actually
  // LANDS (up to half a pill left of its value once it rides the pill), and from the FINAL geometry
  // rather than the animating one: merged and split are different elements, so a mid-entrance flip
  // would remount the marker and its slide would never run.
  const settledYouAt = (onPillPoint ? pillCentrePct(couldBe, boxes.pill, boxes.track) : null) ?? now;
  const merged = Math.abs(settledYouAt - market) <= COMBINE_WITHIN;

  React.useLayoutEffect(() => {
    const track = trackRef.current;
    const pill = pillRef.current;
    if (!track || !pill) {
      setBoxes((prev) =>
        prev.pill === 0 && prev.track === 0 && prev.label === 0 && prev.off === 0
          ? prev
          : { pill: 0, track: 0, label: 0, off: 0 },
      );
      return;
    }
    const read = (): void => {
      const next = {
        pill: pill.getBoundingClientRect().width,
        track: track.getBoundingClientRect().width,
        label: labelRef.current?.getBoundingClientRect().width ?? 0,
        // Translated copy, so its width is measured like everything else here.
        off: offRef.current?.getBoundingClientRect().width ?? 0,
      };
      setBoxes((prev) =>
        prev.pill === next.pill &&
        prev.track === next.track &&
        prev.label === next.label &&
        prev.off === next.off
          ? prev
          : next,
      );
    };
    read();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(read);
    observer.observe(track);
    observer.observe(pill);
    if (labelRef.current) observer.observe(labelRef.current);
    if (offRef.current) observer.observe(offRef.current);
    return () => observer.disconnect();
    // `merged` is in here because it decides WHICH label element renders, so the observed node and
    // the measured width both change with it (the shared bar lists its own `combined` for the same
    // reason). Without it the effect kept observing a node the render had already replaced.
  }, [tierLabel, now, couldBe, merged, market]);

  return (
    <Root className={className}>
      <Label text="You vs Market" tag="span" />
      <TrackArea>
        <Track ref={trackRef}>
          <MarketFill $w={marketW} $onTop={onTop} aria-hidden />
          <ProjectionFill $w={couldBeW} aria-hidden />
          {/* Only at now=0 (inactive / not owned): "You" is stated on the track as an Off pill in the
             inactive tone, the same way the product root's cards state it. Once "now" has real
             progress the projection fill already shows where it stands, and a marker at the start
             would sit under the fill instead of marking anything. */}
          <PillSlot>
            {now <= 0 ? (
              <TierPill ref={offRef} $off>
                <Text text="Off" tag="span" />
              </TierPill>
            ) : null}
            {/* Rides the projection fill's tip, painted ON the fill. As the Off pill's flex SIBLING
                it cannot be drawn over it, however narrow the panel or long the label. */}
            {tierLabel ? (
              <TierPill ref={pillRef} $rides $offPx={now <= 0 ? boxes.off : 0}>
                <Text text={tierLabel} tag="span" />
              </TierPill>
            ) : null}
            <PillGap $at={couldBeW} aria-hidden />
          </PillSlot>
        </Track>
        {merged ? (
          <Marker $left={youAt} $align={youAlign} $onPill={centred != null}>
            <ArrowUp color={marketColor} />
            <MarkerLabelBox ref={labelRef} $shift={labelShift}>
              <MarkerLabel text="You and Market" tag="span" $color={marketColor} />
            </MarkerLabelBox>
          </Marker>
        ) : (
          <>
            <Marker $left={youAt} $align={youAlign} $onPill={centred != null}>
              <ArrowUp color={nowColor} />
              <MarkerLabelBox ref={labelRef} $shift={labelShift}>
                <MarkerLabel text="You" tag="span" $color={nowColor} />
              </MarkerLabelBox>
            </Marker>
            <Marker $left={market} $align={alignFor(market)}>
              <ArrowUp color={marketColor} />
              <MarkerLabel text="Market" tag="span" $color={marketColor} />
            </Marker>
          </>
        )}
      </TrackArea>
    </Root>
  );
};

export default BenchmarkBar;
